<template>

    <div v-if="!reachedMaximumVerificationAttempts">

        <LcForm v-if="!showQuiz"
            :key="formComponentKey"
            :formData="formData"
            :showSkipVerification="showSkipVerification"
            :showProgressBar="false"
            @completed.once="getQuiz()"
            :showRecaptchaVerification="showRecaptchaVerification"
            :shouldEditActiveStep="counts.verification > 0"
        />

        <Quiz v-if="!!showQuiz"
            :quiz="quizData"
            :showSkipVerification="showSkipVerification"
            @getVerified.once="getVerified"
        />
    </div>
    <div v-else class="text-center py-4">
        <p class="mb-3">
            We were unable to verify your identity at this time.
            Don't worry — your application will continue momentarily.
        </p>
        <p class="text-muted mb-4">
            Continuing in {{ countdown }} seconds...
        </p>
        <div class="row">
            <div class="col-4 offset-4">
                <a class="btn btn-lg btn-primary w-100"
                    id="btnSkip"
                    @click.prevent="skipVerification()">{{ getTranslatedText('__next__', 'buttons') }}</a>
            </div>
        </div>
    </div>

</template>

<script setup>
import { ref, computed, watch, inject, onBeforeUnmount } from 'vue';
import LcForm from '@/components/form/LcForm.vue';
import formSteps from '@/form/steps/verification-eid.js';
import Quiz from '@/components/form/member-enrollment/Quiz.vue';
import { formFunctions } from '@/form/helpers/functions.js';
import { useAttemptLimit } from '@/composables/useAttemptLimit.js';
import { clearNotifications } from '@/utils/notifications.js';
import { useInjectedApplicationSettings } from '@/composables/useApplicationSettingsProvider.js';

const emit = defineEmits(['completed']);

const applicationSettings = useInjectedApplicationSettings();

const { app, updateApplication, getTranslatedText } = inject('app');

const formData = { steps: formSteps };
const quizData = ref({});
const requestAttempted = ref(false);
const errors = ref({});
const notifications = ref({});
const formComponentKey = ref(0);
const { counts, hasReachedMax, increment: incrementAttempt, reset: resetAttempt } = useAttemptLimit({ verification: 2 });
const onVerificationStep = ref(false);
const showRecaptchaVerification = ref(true);

const showQuiz = computed(() => {
    return !!requestAttempted.value && Object.keys(quizData.value).length > 0;
});

const showSkipVerification = computed(() => {
    return counts.value.verification > 0;
});

const reachedMaximumVerificationAttempts = computed(() => hasReachedMax('verification'));

const countdown = ref(5); // 5 seconds before continuing when registration/verification fails.
let countdownTimer = null;

watch(reachedMaximumVerificationAttempts, (reached) => {
    if (reached) {
        clearNotifications(applicationSettings);
        countdownTimer = setInterval(() => {
            countdown.value--;
            if (countdown.value <= 0) {
                clearInterval(countdownTimer);
                skipVerification();
            }
        }, 1000);
    }
});

onBeforeUnmount(() => {
    if (countdownTimer) {
        clearInterval(countdownTimer);
    }
});

watch(errors, (value) => {
    applicationSettings.notifications.error = value;
});

watch(notifications, (value) => {
    applicationSettings.notifications.success = value;
});

function handleUnsuccessfulRequest(response) {
    showRecaptchaVerification.value = false;
    quizData.value = {};
    requestAttempted.value = false;
    notifications.value = {};
    const errorNotificationMessage = response == 'fail' ? ['Our verification systems are temporarily down. Please try again later.'] : {...response.messages};
    incrementAttempt('verification');
    errors.value = reachedMaximumVerificationAttempts.value ? {} : errorNotificationMessage;

    if (onVerificationStep.value && !hasReachedMax('verification')) {
        getQuiz(errors.value);
    } else {
        formComponentKey.value += 1;
    }
}

function getQuiz(errorsParam) {
    applicationSettings.loadingAnimationSeconds = -1;

    const payload = {
        ...app.value,
    };

    requestAttempted.value = true;
    errors.value = !!errorsParam ? errorsParam : {};

    formFunctions.apiGetQuiz(payload).then((response) => {
        if (response && !!response.status && response.status == 'success') {
            quizData.value = response;
            if (!onVerificationStep.value) {
                resetAttempt('verification');
                onVerificationStep.value = true;
            }
        } else {
            handleUnsuccessfulRequest(response);
        }
    }).finally(() => {
        applicationSettings.loadingAnimationSeconds = 0;
    });
}

function getVerified(payload) {
    let isSuccessfulVerification = false;
    applicationSettings.loadingAnimationSeconds = -1;
    formFunctions.apiGetVerified(payload).then((response) => {
        if (response && !!response.status && response.status == 'success') {
            isSuccessfulVerification = true;
            applicationSettings.loadingAnimationSeconds = 0;
            notifications.value = {...response.messages};
            errors.value = {};
        } else {
            handleUnsuccessfulRequest(response);
        }
    }).finally(() => {
        applicationSettings.isUserVerified = isSuccessfulVerification;
        if (isSuccessfulVerification) {
            emit('completed');
        }
    });
}

function skipVerification() {
    if (countdownTimer) {
        clearInterval(countdownTimer);
    }
    clearNotifications(applicationSettings);
    applicationSettings.userWantsVerification = false;
}
</script>
